import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import { SortDirection, sort } from '@/components/table/sortHelper';
import { Product } from '@/services/api/contracts/catalog/types';
import { usePoDeviceFee, usePoSelectedProductList } from '@/services/purchaseOrders/usePurchaseOrderService';
import { FunctionComponent, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { PurchaseOrderConfirmOrderDeviceFeeTableRow } from './PurchaseOrderConfirmOrderDeviceFeeTableRow';

export interface PurchaseOrderConfirmOrderDeviceFeeTableData {
  id: string;
  deviceName: string;
  quantity: number;
  feePerDevice: number;
  totalFee: number;
}

const tableHeaders: DaasTableColumn[] = [
  {
    accessor: 'deviceName',
    sortable: true,
    text: 'Device Name'
  },
  {
    accessor: 'quantity',
    sortable: true,
    text: 'Quantity'
  },
  {
    accessor: 'feePerDevice',
    sortable: true,
    text: 'Unit Fee'
  },
  {
    accessor: 'totalFee',
    sortable: true,
    text: 'Total Fee'
  }
];

interface PurchaseOrderConfirmOrderDeviceFeeTableProps {
  products: Product[];
}

export const PurchaseOrderConfirmOrderDeviceFeeTable: FunctionComponent<
  PurchaseOrderConfirmOrderDeviceFeeTableProps
> = ({ products }) => {
  const [sortColumnId, setSortColumnId] = useState<string>(tableHeaders[0].accessor);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);

  const { orderId } = useParams();
  if (!orderId) throw new Error('Missing order id');

  const selectedProducts = usePoSelectedProductList();
  const deviceFee = usePoDeviceFee();

  const tableData: PurchaseOrderConfirmOrderDeviceFeeTableData[] = useMemo(() => {
    if (!deviceFee) {
      return [];
    }

    const data: PurchaseOrderConfirmOrderDeviceFeeTableData[] = [];
    products.forEach(product => {
      const productLookup = selectedProducts.find(item => item.productId === product.id);

      if (!productLookup) {
        return;
      }

      data.push({
        id: product.id,
        deviceName: product.name,
        quantity: productLookup.quantity,
        feePerDevice: deviceFee,
        totalFee: deviceFee * productLookup.quantity
      });
    });

    return sort(data, sortColumnId, sortDirection);
  }, [selectedProducts, sortColumnId, sortDirection, deviceFee]);

  return (
    <DaaSTable
      headers={tableHeaders}
      onSortCallback={(columnId, direction) => {
        setSortColumnId(columnId);
        setSortDirection(direction);
      }}
      sortColumnId={sortColumnId}
      sortDirection={sortDirection}
    >
      {tableData.map(row => (
        <PurchaseOrderConfirmOrderDeviceFeeTableRow data={row} key={row.id} />
      ))}
    </DaaSTable>
  );
};
